import axios from "axios"
import {
  getRefreshToken,
  updateTokensFromRefresh,
  clearAuthData,
  getAccessToken
} from "./tokenManager"
import jwtDefaultConfig from "../auth/jwt/jwtDefaultConfig"
import api_url from "../../services/apiPaths"

const jwtDefault = { ...jwtDefaultConfig }

const REFRESH_URL = `${api_url.ssoStaff}/auth/refresh`

/**
 * Single-flight refresh coordination.
 *
 * When N parallel API calls get 401:
 *  - the first one owns the refresh call
 *  - the rest subscribe to `failedQueue` and wait
 *  - on success: every waiter retries with the new access token
 *  - on failure: every waiter is rejected and we log out once
 */
let isRefreshing = false
let failedQueue = []
let isRedirectingToLogin = false

const processQueue = (error, token = null) => {
  const queue = failedQueue
  failedQueue = []
  queue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token)
    }
  })
}

const enqueueRequest = () =>
  new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject })
  })

const normalizeMessage = (message) => String(message || "").trim().toLowerCase()

const applyAccessToken = (config, accessToken) => {
  config.headers = config.headers || {}
  config.headers.Authorization = `${jwtDefault.tokenType} ${accessToken}`
  return config
}

/**
 * Detect access-token expiry from an API error/response payload.
 * Matches: { response_code: 401, error_message: "token expired" }
 */
export const isAccessTokenExpiredResponse = (payload = {}) => {
  const code = payload?.response_code ?? payload?.status
  const message = normalizeMessage(payload?.error_message || payload?.message)
  if (Number(code) !== 401) return false
  return message.includes("token expired") && !message.includes("refresh token")
}

/**
 * Detect refresh-token failure that requires forced logout.
 * Matches: "refresh token expired" | "invalid refresh token"
 */
export const isRefreshTokenInvalidResponse = (payload = {}) => {
  const code = payload?.response_code ?? payload?.status
  const message = normalizeMessage(payload?.error_message || payload?.message)
  if (Number(code) !== 401 && !message) return false
  return (
    message.includes("refresh token expired") ||
    message.includes("invalid refresh token")
  )
}

/**
 * True when an axios error represents an access-token expiry / unauthorized that
 * should attempt refresh (HTTP 401, or body response_code 401).
 */
export const shouldAttemptTokenRefresh = (error) => {
  const { response, config } = error || {}
  if (!config) return false

  const status = response?.status
  const data = response?.data || {}

  if (status === 401) return true
  if (Number(data?.response_code) === 401) return true
  return isAccessTokenExpiredResponse(data)
}

/**
 * Clear session and redirect to login exactly once.
 */
export const forceLogoutToLogin = (reason) => {
  if (isRedirectingToLogin) return reason

  isRedirectingToLogin = true
  clearAuthData()

  try {
    localStorage.removeItem("accessToken")
    localStorage.removeItem("refreshToken")
    localStorage.removeItem("uniqueId")
  } catch (_) {
    // ignore storage errors
  }

  if (typeof window !== "undefined" && !window.location.href.includes("/login")) {
    window.location.href = "/#/login"
  }

  return reason
}

/**
 * Call refresh token API and update stored tokens.
 * POST /sso/api/v1/staff/auth/refresh
 *
 * Success: { response_code: 200, data: { access_token, refresh_token } }
 * Failure: { response_code: 401, error_message: "refresh token expired" | "invalid refresh token" }
 *
 * @returns {Promise<string>} New access token
 */
export const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    const err = new Error("No refresh token available")
    err.isAuthFailure = true
    throw err
  }

  let response
  try {
    response = await axios.post(
      REFRESH_URL,
      { refresh_token: refreshToken },
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        }
      }
    )
  } catch (error) {
    const payload = error?.response?.data || {}
    const authFailure =
      error?.response?.status === 401 || isRefreshTokenInvalidResponse(payload)

    const err = new Error(
      payload?.error_message || payload?.message || "Failed to refresh token"
    )
    err.isAuthFailure = authFailure
    err.payload = payload
    throw err
  }

  const payload = response?.data || {}

  if (payload.response_code === 200 && payload?.data?.access_token) {
    updateTokensFromRefresh(payload.data)
    return payload.data.access_token
  }

  if (isRefreshTokenInvalidResponse(payload) || Number(payload.response_code) === 401) {
    const err = new Error(payload?.error_message || "Invalid refresh token")
    err.isAuthFailure = true
    err.payload = payload
    throw err
  }

  const err = new Error(payload?.error_message || "Failed to refresh token")
  err.isAuthFailure = true
  err.payload = payload
  throw err
}

/**
 * Shared single-flight refresh used by non-interceptor callers if needed.
 * Prefer handleUnauthorizedWithRefresh for API 401 handling.
 */
export const refreshAccessTokenWithQueue = () => {
  if (isRefreshing) {
    return enqueueRequest()
  }

  isRefreshing = true

  return refreshAccessToken()
    .then((token) => {
      processQueue(null, token)
      return token
    })
    .catch((error) => {
      processQueue(error, null)
      throw error
    })
    .finally(() => {
      isRefreshing = false
    })
}

/**
 * Access-token expiry handler with single-flight refresh + retry queue.
 *
 * Flow when many APIs 401 at once:
 *  1. First request triggers POST /sso/auth/refresh
 *  2. Other requests wait in failedQueue (no extra refresh calls)
 *  3. Refresh success → all waiting requests retry with new access token
 *  4. Refresh failure → all waiting requests reject + user logged out once
 *
 * A 401 after a successful refresh/retry is treated as a resource failure and
 * does NOT clear the session.
 *
 * @param {import("axios").AxiosError} error
 * @param {import("axios").AxiosInstance} axiosInstance
 * @returns {Promise}
 */
export const handleUnauthorizedWithRefresh = async (error, axiosInstance) => {
  const originalRequest = error?.config

  if (!originalRequest || !shouldAttemptTokenRefresh(error)) {
    return Promise.reject(error)
  }

  const isRefreshRequest =
    originalRequest.url?.includes("/auth/refresh") ||
    originalRequest.url?.includes("/auth/refresh")

  if (isRefreshRequest) {
    forceLogoutToLogin("refresh endpoint unauthorized")
    return Promise.reject(error)
  }

  // Already retried once with a fresh token — keep session, surface API error
  if (originalRequest._retry) {
    return Promise.reject(error)
  }

  if (!getRefreshToken()) {
    forceLogoutToLogin("missing refresh token")
    return Promise.reject(error)
  }

  originalRequest._retry = true

  // Another refresh is already in flight — wait, then retry this request
  if (isRefreshing) {
    try {
      const token = await enqueueRequest()
      applyAccessToken(originalRequest, token)
      return axiosInstance(originalRequest)
    } catch (queueError) {
      return Promise.reject(queueError)
    }
  }

  // This request owns the single refresh call
  isRefreshing = true

  try {
    const newAccessToken = await refreshAccessToken()
    processQueue(null, newAccessToken)
    applyAccessToken(originalRequest, newAccessToken)
    return axiosInstance(originalRequest)
  } catch (refreshError) {
    processQueue(refreshError, null)
    forceLogoutToLogin(refreshError?.message || "refresh failed")
    return Promise.reject(refreshError)
  } finally {
    isRefreshing = false
  }
}

/**
 * Helper for success interceptors when APIs return HTTP 200 with
 * { response_code: 401, error_message: "token expired" }.
 */
export const handleTokenExpiredSuccessResponse = async (response, axiosInstance) => {
  if (!isAccessTokenExpiredResponse(response?.data)) {
    return response
  }

  const fakeError = {
    response: {
      status: 401,
      data: response.data
    },
    config: response.config
  }

  return handleUnauthorizedWithRefresh(fakeError, axiosInstance)
}

export const hasActiveAccessToken = () => !!getAccessToken()
